import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import authServices, {
  isTooManySessionsError,
} from "@workspace/shared/core/services/auth/auth.services";
import {
  SESSION_EXPIRED_EVENT,
  SIGNED_IN_AGAIN_EVENT,
  type SessionExpiredReason,
} from "@workspace/shared/core/services/api";
import { forgetAllHeaderDatePicks } from "@workspace/shared";
import {
  authStore,
  getStoredUserEmail,
  isLoggedIn,
} from "@workspace/shared/core/store/auth/auth.store";
import { IAuth } from "@workspace/shared/core/types";
import profileServices from "@workspace/shared/core/services/profile/profile.services";
import { createPortal } from "react-dom";
import { SUPPORT_STORAGE_PREFIX } from "../../components/support/supportHistory.ts";

interface AuthContextType {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  errorName?: string;
  loading: boolean;
  refreshToken: () => Promise<void>;
  isAuthenticated: boolean | null;
  isAuthLoading: boolean;
  setIsAuthenticated: (v: boolean) => void;
}

interface LoginInterface {
  username: string;
  password: string;
}

/**
 * "Not now" holds the sign-in prompt back this long. Every request keeps
 * failing until the coach signs in (the pending sync runs every 10 s), so
 * without it the prompt was back before the next rep. A sign-out from another
 * device still shows through it: that is news to the coach. (In-game parity.)
 */
export const SIGN_IN_AGAIN_SNOOZE_MS = 2 * 60_000;

const REAUTH_COPY: Record<SessionExpiredReason, { title: string; body: string }> = {
  expired: {
    title: "Session Expired",
    body: "Your session has expired, so your data can\u2019t be saved to the server right now.",
  },
  released: {
    title: "Signed Out",
    body: "This device was signed out from another device, so your data can\u2019t be saved to the server right now.",
  },
};

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}
export const AuthProvider = ({ children }: AuthProviderProps) => {
  // The dashboard wraps each embedded role app in its own AuthProvider, so
  // inside an app there are TWO nested providers. Only the OUTERMOST one may
  // own the session-expired re-login modal — otherwise one window event
  // stacks N identical modals, and signing in closes the top one while its
  // twin behind it looks like the modal "never closed".
  const hasParentProvider = useContext(AuthContext) !== null;
  const { setAuthState, clearAuthState, refreshToken } = authStore();
  const inFlightRef = useRef<boolean>(false);
  const [errorName, setErrorName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [showRevokeSessionsModal, setShowRevokeSessionsModal] = useState(false);
  const [pendingLogin, setPendingLogin] = useState<LoginInterface | null>(null);
  const [revokeLoading, setRevokeLoading] = useState(false);

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isOnline, setIsOnline] = useState<boolean>(() =>
    typeof navigator === "undefined" ? true : navigator.onLine
  );

  // In-place re-login after the session expires (401 + dead refresh token).
  // Swaps tokens via onLogin WITHOUT the destructive logout path, so plays
  // and games still waiting to upload from this device are never wiped.
  // The inputs are uncontrolled and read via refs at submit: browser/password-
  // manager autofill doesn't fire React change events, so controlled state
  // would stay empty and block the submit.
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [reauthReason, setReauthReason] = useState<SessionExpiredReason>("expired");
  const [reauthEmail, setReauthEmail] = useState("");
  const [reauthError, setReauthError] = useState("");
  const [reauthLoading, setReauthLoading] = useState(false);
  const reauthEmailRef = useRef<HTMLInputElement>(null);
  const reauthPasswordRef = useRef<HTMLInputElement>(null);
  // Mirrors showReauthModal for the window-event handler: a repeat
  // session-expired event while the modal is already open must not clobber a
  // visible error message or the coach's typed input.
  const showReauthModalRef = useRef(false);
  showReauthModalRef.current = showReauthModal;
  // True while the revoke-sessions dialog was opened FROM the reauth modal
  // (in-app, no login page mounted). Failure/cancel there must route feedback
  // back to the reauth modal — errorName is only rendered by login pages.
  const reauthHandoffRef = useRef(false);
  // "Not now" until this moment (see SIGN_IN_AGAIN_SNOOZE_MS).
  const reauthSnoozedUntilRef = useRef(0);
  const snoozeReauth = useCallback(() => {
    reauthSnoozedUntilRef.current = Date.now() + SIGN_IN_AGAIN_SNOOZE_MS;
    setShowReauthModal(false);
  }, []);
  // Signed in again in place: the prompt may come back right away next time,
  // and the app registers this device's new session (SessionProvider).
  const announceSignedInAgain = useCallback(() => {
    reauthSnoozedUntilRef.current = 0;
    window.dispatchEvent(new Event(SIGNED_IN_AGAIN_EVENT));
  }, []);

  const login = async (username: string, password: string) => {
    setLoading(true);
    setErrorName("");
    await onLogin({ username, password });
    setLoading(false);
  };

  const checkSession = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const loggedIn = isLoggedIn();
    if (!loggedIn) {
      setIsAuthenticated(false);
      setIsAuthLoading(false);
      inFlightRef.current = false;
      return;
    }

    // Offline: trust persisted auth state, skip network validation
    if (!isOnline) {
      setIsAuthenticated(true);
      setIsAuthLoading(false);
      inFlightRef.current = false;
      return;
    }

    try {
      await profileServices.onGetProfile();
      setIsAuthenticated(true);
    } catch (err) {
      // Network or API issues: keep user signed-in based on stored session
      console.warn("Profile check failed; keeping existing session", err);
      setIsAuthenticated(true);
    } finally {
      setIsAuthLoading(false);
      inFlightRef.current = false;
    }
  }, [isOnline]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      checkSession();
    };
    const handleOffline = () => {
      setIsOnline(false);
      setIsAuthenticated(isLoggedIn());
      setIsAuthLoading(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [checkSession]);

  // The api interceptor fires this when a 401 can't be healed by the refresh
  // token. Prompt for the password in place — NOT the logout flow, which
  // wipes local data. Outermost provider only (see hasParentProvider).
  useEffect(() => {
    if (hasParentProvider) return;
    const onSessionExpired = (event: Event) => {
      if (!isLoggedIn()) return; // login screen: its own error handling applies
      const reason: SessionExpiredReason =
        (event as CustomEvent<{ reason?: SessionExpiredReason }>).detail?.reason === "released"
          ? "released"
          : "expired";
      // Already open: a repeat 401 must not wipe a visible error message or
      // the coach's in-progress typing (a sign-out from elsewhere still
      // updates the wording).
      if (showReauthModalRef.current) {
        if (reason === "released") setReauthReason("released");
        return;
      }
      if (reason === "expired" && Date.now() < reauthSnoozedUntilRef.current) return;
      setReauthReason(reason);
      // Prefer the CURRENT token's email — a stale value from a previous
      // account on this device must not win over the signed-in user's email.
      setReauthEmail((current) => getStoredUserEmail() || current);
      setReauthError("");
      setShowReauthModal(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [hasParentProvider]);

  // It can open while the coach is typing (a note, a player's name): their
  // next keys must not land in the password and Enter send them. Then
  // nothing takes focus; otherwise the password does. (Was autoFocus.)
  useEffect(() => {
    if (!showReauthModal || hasParentProvider) return;
    const active = document.activeElement;
    const typing =
      active instanceof HTMLElement &&
      !reauthPasswordRef.current?.form?.contains(active) &&
      (active.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(active.tagName));
    if (typing) active.blur();
    else reauthPasswordRef.current?.focus();
  }, [showReauthModal, hasParentProvider]);

  const clearUserCookie = () => {
    try {
      const expire = "Thu, 01 Jan 1970 00:00:00 GMT";
      const host = window.location.hostname.replace(/^www\./, "");
      const domains = ["", host ? `.${host}` : "", host];
      const paths = ["/", window.location.pathname.split("/").slice(0, 2).join("/") || "/"];
      for (const domain of domains) {
        for (const path of paths) {
          const domainPart = domain ? ` domain=${domain};` : "";
          document.cookie = `user=; expires=${expire}; path=${path};${domainPart}`;
          document.cookie = `user=; expires=${expire}; path=${path};${domainPart} SameSite=Lax;`;
          document.cookie = `user=; expires=${expire}; path=${path};${domainPart} SameSite=None; Secure;`;
        }
      }
    } catch (e) {
      console.warn("Failed to clear user cookie", e);
    }
  };

  const clearCrossAppPersistedData = () => {
    try {
      if (typeof localStorage === "undefined") {
        return;
      }

      const explicitKeys = [
        "persist:root",
        "persist:qb-root",
        "persist:st-root",
        "cachedGames",
        "pendingGames",
        "pendingPlays",
        "pendingSettings",
        "pendingSaves",
        "qb_pendingUpdatedPlays",
        "st_pendingUpdatedPlays",
      ];

      explicitKeys.forEach((key) => localStorage.removeItem(key));
      // The report PDFs kept for the Library: the next coach on this iPad
      // must not open the previous coach's reports. (The app sidebars clear
      // all of localStorage; the dashboard and QB sign-outs come here.)
      ["practice_pdf_cache", "wandr_pdf_cache"].forEach((key) => localStorage.removeItem(key));

      const dynamicKeys: string[] = [];
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key) continue;
        // persist:* — every practice app has its own redux-persist root
        // (persist:lb-root, persist:ol-root, …). The drawers clear storage
        // BEFORE calling logout, but redux-persist can rewrite its key from
        // in-memory state during logout's network call; this sweep runs
        // right before the hard redirect, so nothing survives.
        // *_pending* — per-app offline queues (wr_pendingPlays, …).
        if (
          key.startsWith("cachedGame:") ||
          key.startsWith("persist:") ||
          key.includes("_pending") ||
          // The QB / OL / ST AI report PDFs (qb_reports_pdf_cache_…).
          key.includes("_reports_pdf_cache_") ||
          // The support chat's history: the next coach on this iPad must not
          // see it, have it sent to the assistant, or attached to a ticket.
          key.startsWith(SUPPORT_STORAGE_PREFIX)
        ) {
          dynamicKeys.push(key);
        }
      }
      dynamicKeys.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Failed clearing shared persisted app data", error);
    }
    try {
      // The AI report PDFs fall back to sessionStorage when localStorage is
      // full; this tab outlives the sign-out, so they would too.
      if (typeof sessionStorage !== "undefined") {
        const sessionKeys: string[] = [];
        for (let index = 0; index < sessionStorage.length; index += 1) {
          const key = sessionStorage.key(index);
          if (key?.includes("_reports_pdf_cache_")) sessionKeys.push(key);
        }
        sessionKeys.forEach((key) => sessionStorage.removeItem(key));
      }
    } catch (error) {
      console.warn("Failed clearing session report caches", error);
    }
    // A header date picked in any app must not greet whoever signs in next.
    forgetAllHeaderDatePicks();
  };

  const logout = async () => {
    try {
      await authServices.onLogout({
        refreshToken: refreshToken || "",
      });
    } catch (error) {
      console.warn("API logout failed, continuing cleanup", error);
    }

    try {
      clearAuthState();
      if (typeof localStorage !== "undefined") {
        localStorage.removeItem("admin-app-auth-storage");
      }
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("admin-app-auth-storage");
      }
      clearCrossAppPersistedData();
      clearUserCookie();
    } catch (e) {
      console.warn("Local cleanup failed", e);
    }

    window.location.href = "/";
  };

  const onLogin = useCallback(async (payload: LoginInterface) => {
    try {
      const { data } = await authServices.onLogin({
        email: payload.username,
        password: payload.password,
      });
      Object.keys(data).forEach((key) => {
        setAuthState({
          key: key as keyof IAuth,
          value: data[key as keyof IAuth],
        });
      });
      setIsAuthenticated(true);
      setShowRevokeSessionsModal(false);
      setPendingLogin(null);
    } catch (error: unknown) {
      if (isTooManySessionsError(error)) {
        setPendingLogin(payload);
        setShowRevokeSessionsModal(true);
        setErrorName("");
      } else {
        setErrorName(error instanceof Error ? error.message : "Invalid credentials");
      }
      throw error;
    }
  }, [setAuthState]);

  const handleRevokeAndLogin = useCallback(async () => {
    if (!pendingLogin) return;
    setRevokeLoading(true);
    try {
      await authServices.onRevokeOtherSessions({
        email: pendingLogin.username,
        password: pendingLogin.password,
      });
      await onLogin(pendingLogin);
      // Reached from the in-app prompt: this was a sign-in again in place.
      if (reauthHandoffRef.current) announceSignedInAgain();
      reauthHandoffRef.current = false;
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Error revoking sessions";
      setErrorName(message);
      setShowRevokeSessionsModal(false);
      setPendingLogin(null);
      // Reached from the in-app reauth modal: errorName has no renderer there
      // (only login pages show it), so a silent close would leave the coach
      // with a dead session and zero feedback. Re-open the reauth modal with
      // the error instead.
      if (reauthHandoffRef.current) {
        reauthHandoffRef.current = false;
        setReauthError(message);
        setShowReauthModal(true);
      }
    } finally {
      setRevokeLoading(false);
    }
  }, [pendingLogin, onLogin, announceSignedInAgain]);

  const closeRevokeModal = useCallback(() => {
    setShowRevokeSessionsModal(false);
    setPendingLogin(null);
    setErrorName("");
    setLoading(false);
    // Cancelling a handoff-opened revoke dialog: the session is still dead —
    // return the coach to the reauth modal rather than silently to the app.
    if (reauthHandoffRef.current) {
      reauthHandoffRef.current = false;
      setReauthError("");
      setShowReauthModal(true);
    }
  }, []);

  const handleReauthSubmit = useCallback(async () => {
    if (reauthLoading) return;
    // Read straight from the DOM so autofilled values (which never fire React
    // change events) are picked up.
    const email = reauthEmailRef.current?.value.trim() ?? "";
    const password = reauthPasswordRef.current?.value ?? "";
    if (!email || !password) {
      setReauthError("Enter your email and password");
      return;
    }
    // This modal re-validates the CURRENT coach; it is not a way to switch
    // accounts. The device's unsynced plays and offline queues belong to the
    // signed-in coach — signing in here as someone else would upload them
    // under the wrong account. Switching goes through Sign Out, which flushes
    // that work first.
    const signedInEmail = getStoredUserEmail().trim().toLowerCase();
    if (signedInEmail && email.toLowerCase() !== signedInEmail) {
      setReauthError(
        `This device is signed in as ${signedInEmail}. Sign out to use a different account.`
      );
      return;
    }
    setReauthLoading(true);
    setReauthError("");
    try {
      // onLogin only writes fresh tokens into authStore — persisted plays,
      // games and offline queues are untouched, and the app's own sync
      // uploads them once requests succeed again.
      await onLogin({ username: email, password });
      setShowReauthModal(false);
      announceSignedInAgain();
    } catch (error: unknown) {
      if (isTooManySessionsError(error)) {
        // onLogin already opened the revoke-sessions modal; hand over to it
        // and remember the origin so failure/cancel routes back here.
        reauthHandoffRef.current = true;
        setShowReauthModal(false);
      } else {
        setReauthError(
          error instanceof Error && error.message
            ? error.message
            : "Invalid credentials"
        );
      }
    } finally {
      setReauthLoading(false);
    }
  }, [reauthLoading, onLogin, announceSignedInAgain]);

  return (
    <AuthContext.Provider
      value={{
        logout,
        login,
        errorName,
        loading,
        refreshToken: async () => {},
        isAuthenticated,
        isAuthLoading,
        setIsAuthenticated,
      }}
    >
      {children}
      {/* Custom overlay instead of SimpleModal: SimpleModal renders at z-50,
        * which app chrome (sidebar drawers z-[99990], the reauth modal
        * z-[125000]) completely buries when this opens IN-APP via the reauth
        * handoff — the coach would see the sign-in prompt vanish with no next
        * step. Portaled to body, same z/blur family as the other dialogs;
        * pointer-events:auto escapes any Radix body pointer-events lock. */}
      {showRevokeSessionsModal &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[125000] flex items-center justify-center bg-black/25 px-6 backdrop-blur-[1.25px]"
            style={{ pointerEvents: "auto" }}
            role="dialog"
            aria-modal="true"
          >
            <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
              <h2 className="mb-2 text-lg font-semibold text-gray-900">
                Sessions on other devices
              </h2>
              <p className="mb-5 text-left text-sm text-gray-600">
                You have one or more sessions open on another device. Do you
                want to continue on this one? The other sessions will be
                closed.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeRevokeModal}
                  disabled={revokeLoading}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleRevokeAndLogin()}
                  disabled={revokeLoading}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  {revokeLoading ? "Processing..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {!hasParentProvider &&
        showReauthModal &&
        typeof document !== "undefined" &&
        createPortal(
          /* Overlay matches the apps' modals (in-game style: bg-black/25 +
           * subtle blur). Sits ABOVE the sidebar drawers (z-[99990]) and
           * BELOW the portaled warnings (z-[130000]) so "Cancel" on a blocked
           * sign-out lands the coach directly on this prompt. */
          <div
            className="fixed inset-0 z-[125000] flex items-center justify-center bg-black/25 px-6 backdrop-blur-[1.25px]"
            style={{ pointerEvents: "auto" }}
            role="dialog"
            aria-modal="true"
            onKeyDown={(e) => {
              if (e.key === "Escape" && !reauthLoading) snoozeReauth();
            }}
          >
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-2 text-lg font-semibold text-gray-900">
              {REAUTH_COPY[reauthReason].title}
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              {REAUTH_COPY[reauthReason].body} Sign in again to keep working —
              everything saved on this device will be kept and uploaded.
            </p>
            {/* A real <form> is what Safari/iOS password managers key on to
              * offer saved credentials — bare inputs don't reliably get the
              * autofill affordance. Submit also gives Enter-to-sign-in. */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void handleReauthSubmit();
              }}
            >
              <div className="mb-5 flex flex-col gap-3 text-left">
                <input
                  ref={reauthEmailRef}
                  type="email"
                  name="email"
                  autoComplete="username"
                  defaultValue={reauthEmail}
                  placeholder="Email"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                />
                <input
                  ref={reauthPasswordRef}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                />
                {reauthError && (
                  <p className="text-sm text-red-600">{reauthError}</p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={snoozeReauth}
                  disabled={reauthLoading}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                >
                  Not now
                </button>
                <button
                  type="submit"
                  disabled={reauthLoading}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60"
                >
                  {reauthLoading ? "Signing in..." : "Sign In"}
                </button>
              </div>
            </form>
          </div>
        </div>,
          document.body
        )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext) as AuthContextType;
